import { Search } from "lucide-react"
import { ChangeEvent, forwardRef, KeyboardEventHandler } from "react"
import {
  formatGiftCardNumberInputDisplay,
  normalizeGiftCardNumberInput,
} from "shared-ts/utils/giftCardNumberInput"
import { Button } from "shared-web/components/base/control/Button"
import { Card } from "shared-web/components/base/form/Card"
import { TextInput } from "shared-web/components/base/input/text/TextInput"

import { formatStatus } from "features/pos/giftcards/format"

import type { GiftCardDetails } from "./useGiftCardInput"

/**
 * When focus is inside a gift card input, `useBarcodeScanner` should let scans
 * type into the field (they populate the card number) instead of routing them
 * to product lookup / the add-product modal.
 */
export const GIFT_CARD_SCAN_CAPTURE_SELECTOR = "[data-gift-card-scan-capture]"

/** Spread onto the gift card input wrapper so the scanner skips pipe capture. */
export const giftCardScanCaptureDomProps = {
  "data-gift-card-scan-capture": true,
} as const

export interface GiftCardInputProps {
  cardNumber: string
  onCardNumberChange: (value: string) => void
  onUseGiftCard: () => void
  giftCardDetails?: GiftCardDetails
  isLoadingBalance: boolean
  error?: string
  disabled?: boolean
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>
  autoFocus?: boolean
  "data-autofocus"?: boolean
}

export const GiftCardInput = forwardRef<HTMLInputElement, GiftCardInputProps>(
  function GiftCardInput(
    {
      cardNumber,
      onCardNumberChange,
      onUseGiftCard,
      giftCardDetails,
      isLoadingBalance,
      error,
      disabled = false,
      onKeyDown,
      autoFocus = false,
      "data-autofocus": dataAutofocus,
    },
    ref
  ) {
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      onCardNumberChange(normalizeGiftCardNumberInput(e.target.value))
    }

    const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
      if (e.key === "Enter" && !isLoadingBalance) {
        e.preventDefault()
        onUseGiftCard()
      }
      onKeyDown?.(e)
    }

    const isBalanceLoaded = giftCardDetails != null

    return (
      <div className="flex flex-col gap-y-4" {...giftCardScanCaptureDomProps}>
        {/* Manual entry section */}
        <Card className="border-v2-border-default bg-v2-background-elevation-raised flex flex-col gap-y-3">
          <div className="text-label">Enter manually, or barcode scan</div>
          <TextInput
            ref={ref}
            placeholder="Enter gift card number"
            value={formatGiftCardNumberInputDisplay(cardNumber)}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            autoFocus={autoFocus}
            errorMessage={error}
            data-autofocus={dataAutofocus}
          />
          {!isBalanceLoaded ? (
            <Button
              mode="secondary"
              size="small"
              onClick={onUseGiftCard}
              loading={isLoadingBalance}
              disabled={disabled || !cardNumber || isLoadingBalance}
              leftIcon={Search}
              className="self-start"
            >
              Look Up Gift Card
            </Button>
          ) : (
            <div className="flex items-center gap-x-2">
              {formatStatus(giftCardDetails.status)}
              <div className="text-body-strong">
                Balance: {giftCardDetails.balance.format()}
              </div>
            </div>
          )}
        </Card>
      </div>
    )
  }
)
